/**
 * BatteryIndicator — badge discreto que aparece apenas se a bateria
 * do dispositivo estiver baixa OU em carregamento.
 *
 * Usa a Battery Status API (`navigator.getBattery()`). Em navegadores
 * sem suporte (iOS Safari, por exemplo) o componente retorna `null`
 * silenciosamente — não polui a UI nem quebra.
 *
 * Regras de exibição:
 *   - Em carregamento                  → badge verde "⚡ Carregando (X%)"
 *   - level < 10% e não carregando     → badge vermelho crítico
 *   - level < 20% e não carregando     → badge amarelo aviso
 *   - caso contrário                   → não renderiza nada
 *
 * Estilo: Plus Jakarta Sans 8px, border 1px colorido conforme severidade,
 * padding compacto. Pensado pra ser usado dentro de um header
 * `position: relative` (o componente assume `position: absolute`).
 */
import React from 'react';

// ─── Tipagem da Battery Status API ──────────────────────────────────
// Não vem incluída em `lib.dom.d.ts` em todas as versões do TS,
// então declaramos localmente (mínimo necessário pro nosso uso).
interface BatteryManager extends EventTarget {
  readonly level: number;        // 0..1
  readonly charging: boolean;
  readonly chargingTime: number; // segundos (Infinity se desconhecido)
  readonly dischargingTime: number;
  addEventListener(type: 'levelchange' | 'chargingchange', listener: () => void): void;
  removeEventListener(type: 'levelchange' | 'chargingchange', listener: () => void): void;
}

interface NavigatorWithBattery extends Navigator {
  getBattery?: () => Promise<BatteryManager>;
}

interface BatteryIndicatorProps {
  /** Função de tradução i18n. Aceita chave + dicionário opcional. */
  readonly t: (key: string, vars?: Record<string, string | number>) => string;
}

interface BatteryState {
  readonly level: number;     // 0..1
  readonly charging: boolean;
}

/**
 * Decide qual variante exibir a partir do estado da bateria.
 * Retorna `null` quando não há nada relevante pra mostrar.
 */
function decideVariant(
  state: BatteryState | null
): 'charging' | 'critical' | 'low' | null {
  if (!state) return null;
  if (state.charging) return 'charging';
  if (state.level < 0.10) return 'critical';
  if (state.level < 0.20) return 'low';
  return null;
}

export function BatteryIndicator({ t }: BatteryIndicatorProps) {
  const [state, setState] = React.useState<BatteryState | null>(null);
  const [supported, setSupported] = React.useState<boolean>(true);

  React.useEffect(() => {
    const nav = navigator as NavigatorWithBattery;
    if (typeof nav.getBattery !== 'function') {
      // Sem suporte (iOS Safari etc) — não renderiza nada.
      setSupported(false);
      return;
    }

    let cancelled = false;
    let battery: BatteryManager | null = null;

    const sync = () => {
      if (cancelled || !battery) return;
      setState({ level: battery.level, charging: battery.charging });
    };

    nav.getBattery()
      .then((b) => {
        if (cancelled) return;
        battery = b;
        sync();
        battery.addEventListener('levelchange', sync);
        battery.addEventListener('chargingchange', sync);
      })
      .catch(() => {
        // Promise pode rejeitar em alguns contextos (ex: contextos não
        // seguros). Falha silenciosa — comporta como "não suportado".
        if (!cancelled) setSupported(false);
      });

    return () => {
      cancelled = true;
      if (battery) {
        battery.removeEventListener('levelchange', sync);
        battery.removeEventListener('chargingchange', sync);
      }
    };
  }, []);

  if (!supported) return null;

  const variant = decideVariant(state);
  if (!variant || !state) return null;

  const pct = Math.round(state.level * 100);

  // Mapa variante → (cor, label) — calculado em runtime pra usar `t`.
  let color: string;
  let label: string;
  switch (variant) {
    case 'charging':
      color = 'var(--violet)';
      label = t('mobile.battery.charging', { pct });
      break;
    case 'critical':
      color = '#DC2626';
      label = t('mobile.battery.critical_warning', { pct });
      break;
    case 'low':
    default:
      color = '#FFC107';
      label = t('mobile.battery.low_warning', { pct });
      break;
  }

  return (
    <div
      role="status"
      aria-live="polite"
      aria-label={label}
      style={{
        position: 'absolute',
        top: '8px',
        right: '8px',
        fontFamily: 'var(--font-sans)',
        fontSize: '12px',
        lineHeight: 1.4,
        color,
        border: `1px solid ${color}`,
        background: 'rgba(34, 37, 50, 0.7)',
        padding: '4px 8px',
        borderRadius: '2px',
        letterSpacing: '0.02em',
        whiteSpace: 'nowrap',
        pointerEvents: 'none',
        zIndex: 20,
      }}
    >
      {label}
    </div>
  );
}

export default BatteryIndicator;
